import { useNavigate } from 'react-router-dom';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { Chart } from '@vnetwork/ui/charts';
import { WidgetCard } from '@/pages/dashboard/widgets/WidgetCard';
import type { ActivationTrendPoint } from '@/api/operations-org-hooks';

interface Props {
  trend: ActivationTrendPoint[] | undefined;
  isLoading: boolean;
}

/**
 * 30-day activation trend widget — area chart of new services created per day.
 * X axis ticks every 7 days; Y axis shows raw count.
 */
export function OpsActivationTrendWidget({ trend, isLoading }: Props) {
  const navigate = useNavigate();

  // Shape data for @vnetwork/ui Chart — xKey = "date", series key = "count".
  const chartData = (trend ?? []).map((p) => ({
    date: p.date.slice(5), // "MM-DD" for axis brevity
    count: p.count,
  }));

  return (
    <WidgetCard
      title="Activation trend"
      description="New services created daily — last 30 days"
      actions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/operations/services')}
        >
          View all services →
        </Button>
      }
    >
      {isLoading ? (
        <Skeleton style={{ height: 180 }} />
      ) : (
        <Chart
          type="area"
          data={chartData}
          xKey="date"
          series={[{ key: 'count', label: 'New services', color: 'var(--vn-primary-solid)' }]}
          height={180}
          showGrid
          showXAxis
          showYAxis
          curve="monotone"
          valueFormatter={(v) => String(v ?? 0)}
        />
      )}
    </WidgetCard>
  );
}
